// Deep service content for premium digital systems website

export const servicesData = {
  en: {
    aiSystems: {
      id: 'ai-systems',
      title: 'AI Systems & Automation',
      shortTitle: 'AI Systems',
      tagline: 'Intelligent Infrastructure That Works',
      heroDescription: 'Enterprise-grade AI systems and workflow automation that eliminate inefficiency and scale operations. We build custom pipelines, integrations, and intelligent processes tailored to your business.',
      overview: {
        title: 'Overview',
        content: 'We design, build, and deploy AI-powered systems that transform how organizations operate. From content automation to operational intelligence, our solutions are engineered for reliability, scalability, and measurable ROI. This is not generic AI consulting—this is custom systems architecture built on proven methodologies.'
      },
      whatWeDo: {
        title: 'What We Build',
        items: [
          { title: 'Automation Pipelines', description: 'End-to-end workflows that automate content production, data processing, and operational tasks' },
          { title: 'AI-Enhanced Editing', description: 'Intelligent post-production systems that accelerate video editing while maintaining quality' },
          { title: 'Content Strategy Systems', description: 'AI-driven planning and optimization tools for multi-platform content operations' },
          { title: 'Custom GPT Integrations', description: 'Tailored language model implementations for specific business processes' },
          { title: 'Multi-Language Pipelines', description: 'Automated translation and localization systems for global content distribution' },
          { title: 'Analytics & Intelligence', description: 'AI-powered insights systems that inform strategy and optimize performance' }
        ]
      },
      problemsSolved: {
        title: 'Problems We Solve',
        items: [
          'Manual processes consuming valuable team hours',
          'Inconsistent output quality across content production',
          'Scaling bottlenecks limiting growth potential',
          'Fragmented tools creating operational inefficiency',
          'Lack of data-driven decision making infrastructure',
          'Slow time-to-market for content and campaigns'
        ]
      },
      useCases: {
        title: 'Use Cases',
        items: [
          { title: 'Content Production Automation', description: 'Transform raw ideas into multi-format content automatically. Brief → Script → Video → Distribution across platforms.', industry: 'Media & Publishing' },
          { title: 'Customer Support Intelligence', description: 'AI-powered response systems that handle inquiries, route complex issues, and learn from interactions.', industry: 'E-commerce & SaaS' },
          { title: 'Research & Analysis Pipelines', description: 'Automated data gathering, synthesis, and report generation for market intelligence and competitive analysis.', industry: 'Consulting & Finance' },
          { title: 'Social Media Operations', description: 'Intelligent scheduling, caption generation, and performance optimization across multiple accounts and platforms.', industry: 'Agencies & Brands' },
          { title: 'Document Processing Systems', description: 'Automated extraction, classification, and routing of information from documents and forms.', industry: 'Legal & Healthcare' },
          { title: 'Personalization Engines', description: 'Dynamic content and experience customization based on user behavior and preferences.', industry: 'E-commerce & Education' }
        ]
      },
      process: {
        title: 'Our Process',
        steps: [
          { number: '01', title: 'Discovery & Audit', description: 'We map your current workflows, identify automation opportunities, and define success metrics.' },
          { number: '02', title: 'Architecture Design', description: 'Custom system architecture designed for your specific requirements, integrations, and scale.' },
          { number: '03', title: 'Development & Testing', description: 'Iterative build process with continuous testing and stakeholder feedback loops.' },
          { number: '04', title: 'Deployment & Training', description: 'Careful rollout with comprehensive documentation and team enablement.' },
          { number: '05', title: 'Optimization & Support', description: 'Ongoing refinement based on performance data and evolving requirements.' }
        ]
      },
      deliverables: {
        title: 'What You Receive',
        items: [
          'Custom AI system tailored to your operations',
          'Complete technical documentation',
          'Team training and onboarding materials',
          'Integration with existing tools and platforms',
          'Performance monitoring dashboard',
          'Ongoing support and optimization'
        ]
      },
      addOns: {
        title: 'Advanced Options',
        items: [
          'Multi-language deployment',
          'Advanced security and compliance configuration',
          'Custom reporting and analytics',
          'API development for third-party integrations',
          'Dedicated support and SLA packages'
        ]
      },
      faq: [
        { question: 'How long does implementation typically take?', answer: 'Most AI system implementations take 4-12 weeks depending on complexity. Simple automation pipelines can be deployed in 2-4 weeks, while comprehensive enterprise systems may require 3-6 months.' },
        { question: 'Do we need technical staff to maintain the system?', answer: 'No. Our systems are designed for business users. We provide intuitive interfaces, comprehensive documentation, and ongoing support. Technical maintenance is handled by our team.' },
        { question: 'How do you ensure data security?', answer: 'We implement enterprise-grade security protocols, including encryption, access controls, and compliance with relevant regulations. All systems are designed with security-first architecture.' },
        { question: 'Can AI systems integrate with our existing tools?', answer: 'Yes. We specialize in integration architecture. Most modern platforms can be connected through APIs, webhooks, or custom connectors. We audit your stack during discovery.' },
        { question: 'What ROI can we expect?', answer: 'ROI varies by use case, but typical outcomes include 40-70% reduction in manual processing time, 2-3x increase in content output, and significant error reduction. We define measurable KPIs during planning.' },
        { question: 'Do you use off-the-shelf AI or custom models?', answer: 'We use the best tool for each job. This often means leveraging state-of-the-art foundation models (GPT-4, Claude, etc.) with custom fine-tuning, prompting, and orchestration layers tailored to your needs.' }
      ]
    },
    webDevelopment: {
      id: 'web-development',
      title: 'Web Design & Development',
      shortTitle: 'Websites',
      tagline: 'Digital Platforms Built for Growth',
      heroDescription: 'High-performance web platforms engineered for conversion, scalability, and brand authority. We build digital infrastructure—not just websites.',
      overview: {
        title: 'Overview',
        content: 'Every digital presence we create is architected as a business asset. From corporate sites to complex platforms, we combine strategic UX, premium design, and robust engineering to deliver web experiences that drive measurable results. This is not template customization—this is custom digital infrastructure.'
      },
      whatWeDo: {
        title: 'What We Build',
        items: [
          { title: 'Corporate & Brand Sites', description: 'Premium company websites that establish authority and convert visitors into clients' },
          { title: 'Landing Pages & Campaigns', description: 'High-converting pages optimized for specific offers, launches, and marketing initiatives' },
          { title: 'E-commerce Platforms', description: 'Complete online stores with inventory, payments, and customer management systems' },
          { title: 'Booking & Service Platforms', description: 'Appointment scheduling, service delivery, and client management systems' },
          { title: 'Community & Membership Portals', description: 'Private platforms for courses, communities, and subscription-based services' },
          { title: 'Dashboards & Internal Tools', description: 'Custom admin panels, reporting systems, and operational interfaces' }
        ]
      },
      problemsSolved: {
        title: 'Problems We Solve',
        items: [
          'Outdated websites damaging brand perception',
          'Poor conversion rates despite traffic',
          'Slow load times affecting SEO and user experience',
          'Difficulty updating content without developer help',
          'No clear user journey or call-to-action structure',
          'Mobile experience significantly worse than desktop'
        ]
      },
      useCases: {
        title: 'Use Cases',
        items: [
          { title: 'Agency & Consultancy Sites', description: 'Premium portfolios and service presentations that establish expertise and generate qualified leads.', industry: 'Professional Services' },
          { title: 'Real Estate Marketing', description: 'Property showcases, listing systems, and lead capture platforms for developers and agents.', industry: 'Real Estate' },
          { title: 'Creator & Personal Brands', description: 'Portfolio sites, link hubs, and content platforms for influencers and thought leaders.', industry: 'Media & Entertainment' },
          { title: 'SaaS & Product Sites', description: 'Product marketing sites with demo integrations, pricing pages, and conversion optimization.', industry: 'Technology' },
          { title: 'E-commerce & D2C Brands', description: 'Complete online stores with product management, checkout optimization, and retention systems.', industry: 'Retail' },
          { title: 'Educational Platforms', description: 'Course delivery systems, learning management, and certification infrastructure.', industry: 'Education' }
        ]
      },
      process: {
        title: 'Our Process',
        steps: [
          { number: '01', title: 'Strategy & Discovery', description: 'Business goals, audience analysis, competitive research, and success metrics definition.' },
          { number: '02', title: 'Information Architecture', description: 'Site structure, user flows, content hierarchy, and conversion pathway design.' },
          { number: '03', title: 'UX/UI Design', description: 'Wireframes, visual design, interaction patterns, and responsive layouts.' },
          { number: '04', title: 'Development', description: 'Clean code, performance optimization, CMS integration, and thorough testing.' },
          { number: '05', title: 'Launch & Growth', description: 'Deployment, analytics setup, SEO foundations, and ongoing optimization.' }
        ]
      },
      deliverables: {
        title: 'What You Receive',
        items: [
          'Custom-designed, fully responsive website',
          'Content management system for easy updates',
          'Performance-optimized for Core Web Vitals',
          'SEO technical foundations configured',
          'Analytics and tracking implementation',
          'Documentation and training materials',
          'Post-launch support period'
        ]
      },
      addOns: {
        title: 'Advanced Options',
        items: [
          'Advanced animations and interactions',
          'Multi-language support',
          'Advanced e-commerce features',
          'Custom integrations (CRM, email, etc.)',
          'Ongoing maintenance packages'
        ]
      },
      faq: [
        { question: 'Do you provide hosting and ongoing maintenance?', answer: 'No. We do not provide hosting or ongoing maintenance services. Clients are free to choose and manage their own hosting providers.' },
        { question: 'Do you redesign existing websites?', answer: 'No. We do not redesign or rebuild existing websites. All projects are handled as new builds from scratch.' },
        { question: 'How long does a website project take?', answer: 'Most website projects are completed very quickly. Standard websites: 1 week. Larger or more detailed projects: up to 2 weeks. Maximum timeline under any circumstance: 3 weeks. Exact timelines are confirmed at the start of the project.' },
        { question: 'Do you work with specific platforms?', answer: 'We select technology based on project requirements. Common choices include React/Next.js for custom builds, Webflow for marketing sites, and Shopify for e-commerce. We recommend the best fit for your needs.' },
        { question: 'Can we update content ourselves?', answer: 'Absolutely. Every site includes a user-friendly content management system. We provide training and documentation so your team can make updates confidently.' },
        { question: 'Is SEO included?', answer: 'Yes. All sites include technical SEO foundations: site structure, meta tags, schema markup, performance optimization, and sitemap configuration. Content SEO strategy is available as an add-on.' }
      ]
    },
    contentProduction: {
      id: 'content-production',
      title: 'Professional Editing & Content Production',
      shortTitle: 'Content',
      tagline: 'Broadcast-Quality Content at Scale',
      heroDescription: 'End-to-end video production and post-processing systems. From raw footage to distribution-ready content—optimized workflows that deliver quality and speed.',
      overview: {
        title: 'Overview',
        content: 'We operate production systems, not project-based editing. Our infrastructure handles everything from short-form social content to long-form documentary work, with consistent quality, brand alignment, and rapid turnaround. This is how professional media operations scale.'
      },
      whatWeDo: {
        title: 'What We Build',
        items: [
          { title: 'Short-Form Content Systems', description: 'TikTok, Reels, Shorts—platform-native content engineered for engagement and virality' },
          { title: 'Long-Form Production', description: 'YouTube videos, documentaries, courses—complete post-production from raw footage' },
          { title: 'Brand Content Libraries', description: 'Organized asset systems with templates, graphics, and reusable elements' },
          { title: 'Multi-Platform Adaptation', description: 'Single content transformed for optimal performance across all platforms' },
          { title: 'Motion Graphics & Animation', description: 'Custom animations, lower thirds, transitions, and branded visual elements' },
          { title: 'Podcast & Audio Production', description: 'Audio editing, enhancement, and distribution preparation' }
        ]
      },
      problemsSolved: {
        title: 'Problems We Solve',
        items: [
          'Inconsistent content quality across outputs',
          'Slow turnaround times limiting publishing frequency',
          'Brand inconsistency across platforms',
          'High cost per piece of content',
          'Difficulty scaling content operations',
          'No systematic approach to content production'
        ]
      },
      useCases: {
        title: 'Use Cases',
        items: [
          { title: 'Creator Content Operations', description: 'Systematic production for YouTubers, podcasters, and social media creators requiring consistent output.', industry: 'Creator Economy' },
          { title: 'Brand Social Content', description: 'Ongoing social media content production maintaining brand standards across all touchpoints.', industry: 'Consumer Brands' },
          { title: 'Course & Training Production', description: 'Educational content with clear structure, professional polish, and engagement optimization.', industry: 'Education & Training' },
          { title: 'Corporate Communications', description: 'Internal videos, announcements, and stakeholder communications with executive-level quality.', industry: 'Enterprise' },
          { title: 'Event & Conference Content', description: 'Multi-camera editing, highlight reels, and social clips from live events.', industry: 'Events' },
          { title: 'Product & Marketing Videos', description: 'Product demos, explainers, and promotional content that converts.', industry: 'SaaS & E-commerce' }
        ]
      },
      process: {
        title: 'Our Process',
        steps: [
          { number: '01', title: 'Brief & Planning', description: 'Content goals, brand guidelines, platform requirements, and delivery specifications.' },
          { number: '02', title: 'Asset Intake', description: 'Organized file transfer, footage review, and production planning.' },
          { number: '03', title: 'Editing & Production', description: 'Professional editing with brand consistency and platform optimization.' },
          { number: '04', title: 'Review & Revision', description: 'Structured feedback process with defined revision rounds.' },
          { number: '05', title: 'Delivery & Distribution', description: 'Final export in all required formats, organized delivery, and optional scheduling.' }
        ]
      },
      deliverables: {
        title: 'What You Receive',
        items: [
          'Professionally edited content in all required formats',
          'Platform-optimized exports (aspect ratios, lengths, specs)',
          'Organized project files and assets',
          'Thumbnail designs (for video content)',
          'Caption/subtitle files when applicable',
          'Content performance recommendations'
        ]
      },
      addOns: {
        title: 'Advanced Options',
        items: [
          'Custom motion graphics packages',
          'Music licensing and selection',
          'Multi-language subtitling',
          'Content repurposing systems',
          'Dedicated editor assignment'
        ]
      },
      faq: [
        { question: 'What is your typical turnaround time?', answer: 'Short-form content: 24-72 hours. Long-form videos: 5-10 business days. Rush delivery available for urgent projects. Ongoing retainers include guaranteed turnaround SLAs.' },
        { question: 'How do you maintain brand consistency?', answer: 'We create brand guidelines documents, template systems, and asset libraries. Every editor working on your content follows standardized processes and style guides.' },
        { question: 'What formats do you deliver?', answer: 'All standard formats: MP4, MOV, ProRes, etc. We optimize exports for each platform (TikTok, YouTube, Instagram, LinkedIn) with correct specs, aspect ratios, and compression.' },
        { question: 'Do you handle raw footage from any camera?', answer: 'Yes. We work with footage from phones, DSLRs, cinema cameras, screen recordings, and webcams. We optimize the edit approach based on source material quality.' },
        { question: 'Can you create content from scratch?', answer: 'Yes. Beyond editing existing footage, we can develop concepts, scripts, and storyboards. We also produce motion graphics, animations, and screen-recorded tutorials.' },
        { question: 'How does pricing work?', answer: 'We offer per-project pricing and monthly retainer packages. Retainers provide better rates, guaranteed capacity, and priority turnaround. We recommend retainers for ongoing content needs.' }
      ]
    },
    socialMedia: {
      id: 'social-media',
      title: 'Social Media Strategy & Management',
      shortTitle: 'Social Media',
      tagline: 'Systematic Growth, Not Random Posting',
      heroDescription: 'Strategic social infrastructure and audience development. Platform-specific growth systems, content frameworks, and data-driven optimization for sustainable results.',
      overview: {
        title: 'Overview',
        content: 'Social media success is not about posting more—it is about posting strategically. We build systematic approaches to audience growth, engagement, and conversion that compound over time. No trend-chasing, no vanity metrics—just sustainable growth infrastructure.'
      },
      whatWeDo: {
        title: 'What We Build',
        items: [
          { title: 'Strategy & Positioning', description: 'Clear brand positioning, content pillars, and differentiation strategy for each platform' },
          { title: 'Content Systems', description: 'Repeatable frameworks for content ideation, production, and scheduling' },
          { title: 'Growth Architecture', description: 'Systematic approaches to audience building, engagement, and community development' },
          { title: 'Analytics & Optimization', description: 'Performance tracking, testing frameworks, and data-driven iteration' },
          { title: 'Community Management', description: 'Engagement systems, response protocols, and relationship building' },
          { title: 'Conversion Infrastructure', description: 'Lead capture, funnel design, and offer optimization for social audiences' }
        ]
      },
      problemsSolved: {
        title: 'Problems We Solve',
        items: [
          'Posting consistently but not growing',
          'No clear strategy or content direction',
          'Low engagement despite follower count',
          'Difficulty converting followers to customers',
          'Platform algorithm changes disrupting performance',
          'No time to manage social properly'
        ]
      },
      useCases: {
        title: 'Use Cases',
        items: [
          { title: 'Personal Brand Building', description: 'Positioning executives, founders, and thought leaders as industry authorities through strategic content.', industry: 'Professional Services' },
          { title: 'Brand Social Presence', description: 'Managing multi-platform presence for consumer brands with consistent voice and engagement.', industry: 'Consumer Brands' },
          { title: 'B2B Lead Generation', description: 'LinkedIn-focused strategies that generate qualified leads and partnership opportunities.', industry: 'B2B Services' },
          { title: 'E-commerce Social Commerce', description: 'Shoppable content, influencer coordination, and conversion-focused social strategies.', industry: 'E-commerce' },
          { title: 'Creator Growth Systems', description: 'Audience development, monetization strategy, and platform diversification for creators.', industry: 'Creator Economy' },
          { title: 'Local Business Visibility', description: 'Community-focused social strategies that drive foot traffic and local brand awareness.', industry: 'Local Business' }
        ]
      },
      process: {
        title: 'Our Process',
        steps: [
          { number: '01', title: 'Audit & Analysis', description: 'Current performance review, competitor analysis, and audience research.' },
          { number: '02', title: 'Strategy Development', description: 'Positioning, content pillars, platform prioritization, and growth targets.' },
          { number: '03', title: 'System Setup', description: 'Content calendar, workflow tools, templates, and team processes.' },
          { number: '04', title: 'Execution & Management', description: 'Content production, scheduling, engagement, and community management.' },
          { number: '05', title: 'Analysis & Iteration', description: 'Performance review, testing, optimization, and strategy refinement.' }
        ]
      },
      deliverables: {
        title: 'What You Receive',
        items: [
          'Comprehensive social media strategy document',
          'Content pillar framework and topic calendar',
          'Platform-specific growth playbooks',
          'Monthly content planning and scheduling',
          'Regular performance reports and insights',
          'Ongoing optimization recommendations'
        ]
      },
      addOns: {
        title: 'Advanced Options',
        items: [
          'Influencer partnership coordination',
          'Paid social advertising management',
          'Crisis communication protocols',
          'Employee advocacy programs',
          'Social listening and monitoring'
        ]
      },
      faq: [
        { question: 'How long until we see results?', answer: 'Initial improvements in engagement and consistency appear within 30-60 days. Meaningful follower growth typically requires 3-6 months of consistent execution. We set realistic expectations during strategy.' },
        { question: 'Which platforms do you manage?', answer: 'We specialize in Instagram, TikTok, LinkedIn, YouTube, and Twitter/X. We recommend focusing on 2-3 platforms maximum initially, then expanding once systems are established.' },
        { question: 'Do you create the content or just strategy?', answer: 'Both. We offer strategy-only engagements and full-service management including content production. Most clients benefit from integrated strategy + execution.' },
        { question: 'How do you measure success?', answer: 'We track meaningful metrics: engagement rate, follower growth rate, reach, saves/shares, profile visits, website clicks, and conversions. We define KPIs during strategy based on your goals.' },
        { question: 'What makes your approach different?', answer: 'We build systems, not random content. Everything is strategic: content pillars, posting schedules, engagement protocols, testing frameworks. This creates compounding growth, not sporadic spikes.' },
        { question: 'How involved do we need to be?', answer: 'Depends on the engagement type. Strategy-only requires your execution. Full management requires minimal involvement—typically just approvals and occasional content input.' }
      ]
    },
    publishing: {
      id: 'publishing-systems',
      title: 'Publishing Education & System Setup',
      shortTitle: 'Publishing',
      tagline: 'Complete Creator Business Infrastructure',
      heroDescription: 'Training modules, operational systems, and business infrastructure for creators and businesses entering digital publishing. We build the systems that enable scale.',
      overview: {
        title: 'Overview',
        content: 'Successful digital publishing requires more than content—it requires systems. We help creators and businesses build the complete operational infrastructure needed for sustainable digital publishing: team structures, workflows, monetization, and growth systems.'
      },
      whatWeDo: {
        title: 'What We Build',
        items: [
          { title: 'Creator Business Setup', description: 'Complete infrastructure for launching and scaling a digital publishing operation' },
          { title: 'Team & Role Systems', description: 'Hiring frameworks, role definitions, and team operations for content businesses' },
          { title: 'Workflow & SOP Development', description: 'Documented processes for consistent, scalable content operations' },
          { title: 'Monetization Architecture', description: 'Revenue stream setup: ads, sponsorships, products, memberships, consulting' },
          { title: 'Training & Onboarding Programs', description: 'Educational content and systems for team development' },
          { title: 'Tech Stack Configuration', description: 'Tool selection, integration, and optimization for publishing operations' }
        ]
      },
      problemsSolved: {
        title: 'Problems We Solve',
        items: [
          'Creator burnout from doing everything alone',
          'No clear systems for scaling content production',
          'Difficulty hiring and training team members',
          'Inconsistent monetization and revenue',
          'No documented processes causing errors and inefficiency',
          'Tech overwhelm from too many disconnected tools'
        ]
      },
      useCases: {
        title: 'Use Cases',
        items: [
          { title: 'Solo Creator to Team', description: 'Helping individual creators build their first team with proper systems and workflows.', industry: 'Creator Economy' },
          { title: 'Course Business Launch', description: 'Complete setup for launching educational products: platform, content, marketing, delivery.', industry: 'Education' },
          { title: 'Media Company Operations', description: 'Operational infrastructure for multi-creator or multi-show media operations.', industry: 'Media' },
          { title: 'Corporate Content Teams', description: 'Internal publishing operations for companies building content marketing functions.', industry: 'Enterprise' },
          { title: 'Newsletter & Written Publishing', description: 'Systems for consistent written content production, growth, and monetization.', industry: 'Publishing' },
          { title: 'Podcast Network Setup', description: 'Multi-show podcast operations with shared resources and efficient workflows.', industry: 'Audio Media' }
        ]
      },
      process: {
        title: 'Our Process',
        steps: [
          { number: '01', title: 'Business Assessment', description: 'Current state audit, goal definition, and gap analysis.' },
          { number: '02', title: 'System Architecture', description: 'Design of workflows, team structure, and technology stack.' },
          { number: '03', title: 'Documentation & SOPs', description: 'Creation of comprehensive operational documentation.' },
          { number: '04', title: 'Implementation & Training', description: 'System setup, team training, and process rollout.' },
          { number: '05', title: 'Optimization & Support', description: 'Refinement based on real-world operation and ongoing guidance.' }
        ]
      },
      deliverables: {
        title: 'What You Receive',
        items: [
          'Complete operational playbook for your publishing business',
          'Role descriptions and hiring guides',
          'Documented SOPs for all key processes',
          'Tech stack recommendations and setup guides',
          'Training materials for team onboarding',
          'Monetization strategy and implementation plan'
        ]
      },
      addOns: {
        title: 'Advanced Options',
        items: [
          'Ongoing advisory and coaching',
          'Recruiting support for key hires',
          'Custom training program development',
          'Revenue optimization consulting',
          'Annual system audits and updates'
        ]
      },
      faq: [
        { question: 'Who is this service for?', answer: 'Creators earning $10k+/month looking to scale, businesses launching content operations, and media companies optimizing their infrastructure. We work with those ready to invest in systems.' },
        { question: 'How is this different from coaching?', answer: 'We build actual systems and documentation, not just advice. You receive operational infrastructure: SOPs, templates, workflows, and training materials that your team can execute.' },
        { question: 'What is the typical investment?', answer: 'Engagements range from focused system builds ($5-15k) to comprehensive business infrastructure ($25-50k+). Investment scales with scope and complexity.' },
        { question: 'How long does a typical engagement take?', answer: 'Focused projects: 4-8 weeks. Comprehensive builds: 3-6 months. Ongoing advisory relationships are also available.' },
        { question: 'Do you help with hiring?', answer: 'We provide hiring frameworks, role descriptions, interview guides, and can advise on the process. Direct recruiting services are available as an add-on.' },
        { question: 'What if our business model is unique?', answer: 'Every engagement is customized. We adapt proven frameworks to your specific business model, audience, and goals. Uniqueness is expected.' }
      ]
    },
    brandIdentity: {
      id: 'brand-identity',
      title: 'Brand Identity & Logo Design',
      shortTitle: 'Branding',
      tagline: 'Identity That Commands Attention',
      heroDescription: 'Complete brand architecture from strategy to implementation. Logo systems, visual identity, and comprehensive brand guidelines that build recognition and trust.',
      overview: {
        title: 'Overview',
        content: 'Brand identity is not just a logo—it is a complete visual and strategic system. We develop brand architecture that works across every touchpoint: from business cards to billboards, from social profiles to enterprise presentations. Identity that scales.'
      },
      whatWeDo: {
        title: 'What We Build',
        items: [
          { title: 'Brand Strategy', description: 'Positioning, personality, values, and competitive differentiation framework' },
          { title: 'Logo System', description: 'Primary logo, variations, icons, and usage specifications' },
          { title: 'Visual Identity', description: 'Color systems, typography, imagery style, and graphic elements' },
          { title: 'Brand Guidelines', description: 'Comprehensive documentation ensuring consistent application' },
          { title: 'Application Design', description: 'Business cards, letterheads, presentations, and key collateral' },
          { title: 'Digital Brand Kit', description: 'Social templates, email signatures, and digital asset library' }
        ]
      },
      problemsSolved: {
        title: 'Problems We Solve',
        items: [
          'Inconsistent visual presence across touchpoints',
          'Logo that does not work at all sizes or contexts',
          'No clear guidelines causing brand dilution',
          'Visual identity that does not match business positioning',
          'Difficulty creating on-brand materials quickly',
          'Brand that does not stand out from competitors'
        ]
      },
      useCases: {
        title: 'Use Cases',
        items: [
          { title: 'Startup Brand Creation', description: 'Complete brand identity for new ventures establishing market presence.', industry: 'Startups' },
          { title: 'Brand Refresh', description: 'Evolution of existing identity to match business growth and new positioning.', industry: 'Established Businesses' },
          { title: 'Personal Brand Identity', description: 'Professional visual identity for executives, creators, and thought leaders.', industry: 'Personal Brands' },
          { title: 'Product Brand Development', description: 'Distinct identity for new product lines or sub-brands.', industry: 'Consumer Products' },
          { title: 'Agency & Service Brands', description: 'Premium identity for professional service firms and agencies.', industry: 'Professional Services' },
          { title: 'Non-Profit & Organization Branding', description: 'Identity that communicates mission and builds stakeholder trust.', industry: 'Non-Profit' }
        ]
      },
      process: {
        title: 'Our Process',
        steps: [
          { number: '01', title: 'Discovery & Strategy', description: 'Business understanding, competitor analysis, and brand positioning.' },
          { number: '02', title: 'Concept Development', description: 'Multiple creative directions exploring different visual approaches.' },
          { number: '03', title: 'Design Refinement', description: 'Selected direction developed into complete logo system.' },
          { number: '04', title: 'Identity Extension', description: 'Full visual identity: colors, typography, imagery, elements.' },
          { number: '05', title: 'Guidelines & Delivery', description: 'Comprehensive brand book and organized asset delivery.' }
        ]
      },
      deliverables: {
        title: 'What You Receive',
        items: [
          'Logo files in all required formats (vector, raster, various sizes)',
          'Color palette with specifications (HEX, RGB, CMYK, Pantone)',
          'Typography system with font files and usage guidelines',
          'Brand guidelines document (digital PDF)',
          'Social media templates and profile assets',
          'Basic collateral designs (business card, letterhead)'
        ]
      },
      addOns: {
        title: 'Advanced Options',
        items: [
          'Extended collateral design (presentations, proposals)',
          'Motion graphics and animated logo',
          'Brand voice and messaging guidelines',
          'Website design integration',
          'Ongoing brand management support'
        ]
      },
      faq: [
        { question: 'How long does a brand identity project take?', answer: 'Typical timeline is 4-8 weeks from kickoff to final delivery. This allows proper time for strategy, exploration, refinement, and comprehensive system development.' },
        { question: 'How many logo concepts do you present?', answer: 'We typically present 3-4 distinct creative directions. After selection, we refine the chosen direction through 2-3 revision rounds until perfect.' },
        { question: 'What if we do not like any concepts?', answer: 'Rare but handled. If initial concepts miss the mark, we conduct an additional discovery session and develop new directions. Our process minimizes this risk.' },
        { question: 'Do you trademark logos?', answer: 'We design trademarkable logos and can advise on the process, but legal trademark filing requires working with an intellectual property attorney.' },
        { question: 'Can you work with an existing logo?', answer: 'Yes. We offer brand identity development around existing logos, as well as logo refinement and modernization services.' },
        { question: 'What file formats do we receive?', answer: 'Complete package: AI, EPS, SVG, PDF (vector), PNG and JPG (raster) in multiple sizes. Everything needed for any application.' }
      ]
    },
    tiktokManagement: {
      id: 'tiktok-management',
      title: 'TikTok Management & Growth',
      shortTitle: 'TikTok',
      tagline: 'Systematic TikTok Growth',
      heroDescription: 'Strategic TikTok account management with system-based growth methodology. Content planning, production, posting, and optimization—not trend-chasing, but sustainable audience building.',
      overview: {
        title: 'Overview',
        content: 'TikTok success requires understanding the platform deeply and executing systematically. We manage accounts with a methodology focused on sustainable growth: strategic content planning, consistent quality, algorithmic understanding, and continuous optimization. No gimmicks—real results.'
      },
      whatWeDo: {
        title: 'What We Build',
        items: [
          { title: 'Account Strategy', description: 'Positioning, content pillars, and growth roadmap specific to TikTok' },
          { title: 'Content Planning', description: 'Strategic content calendar balancing trends, evergreen, and brand content' },
          { title: 'Content Production', description: 'TikTok-native video creation optimized for the platform' },
          { title: 'Posting & Optimization', description: 'Strategic scheduling, hashtag strategy, and performance monitoring' },
          { title: 'Community Engagement', description: 'Comment management, duets, stitches, and community building' },
          { title: 'Analytics & Reporting', description: 'Performance tracking, insights, and strategy refinement' }
        ]
      },
      problemsSolved: {
        title: 'Problems We Solve',
        items: [
          'Posting but not growing',
          'Not understanding what content works',
          'No time to create TikTok-specific content',
          'Inconsistent posting killing momentum',
          'Chasing trends without strategy',
          'No clear path to monetization'
        ]
      },
      useCases: {
        title: 'Use Cases',
        items: [
          { title: 'Brand TikTok Launch', description: 'Establishing brand presence on TikTok with strategic positioning and content.', industry: 'Consumer Brands' },
          { title: 'Personal Brand Growth', description: 'Building thought leadership and audience for founders, executives, and experts.', industry: 'Personal Brands' },
          { title: 'E-commerce Product Marketing', description: 'Product showcasing, reviews, and conversion-focused TikTok content.', industry: 'E-commerce' },
          { title: 'Service Business Lead Gen', description: 'Educational and trust-building content that generates qualified inquiries.', industry: 'Professional Services' },
          { title: 'Entertainment & Creator Growth', description: 'Audience building and monetization for entertainment-focused creators.', industry: 'Entertainment' },
          { title: 'Local Business Visibility', description: 'Community-focused TikTok presence driving local awareness and foot traffic.', industry: 'Local Business' }
        ]
      },
      process: {
        title: 'Our Process',
        steps: [
          { number: '01', title: 'Account Audit', description: 'Current performance analysis, competitive review, and opportunity identification.' },
          { number: '02', title: 'Strategy Development', description: 'Positioning, content pillars, posting strategy, and growth targets.' },
          { number: '03', title: 'Content Production', description: 'Ongoing content creation following strategic framework.' },
          { number: '04', title: 'Publishing & Engagement', description: 'Strategic posting, community management, and trend participation.' },
          { number: '05', title: 'Analysis & Optimization', description: 'Weekly performance review and strategy refinement.' }
        ]
      },
      deliverables: {
        title: 'What You Receive',
        items: [
          'TikTok-specific strategy document',
          'Content calendar and planning system',
          'X videos per month (based on package)',
          'Strategic posting and scheduling',
          'Community engagement management',
          'Monthly performance reports',
          'Ongoing optimization and strategy calls'
        ]
      },
      addOns: {
        title: 'Advanced Options',
        items: [
          'TikTok advertising management',
          'Creator collaboration coordination',
          'TikTok Shop setup and management',
          'Live streaming support',
          'Multi-account management'
        ]
      },
      faq: [
        { question: 'How many videos do you post per month?', answer: 'Packages range from 12-30 videos per month. Optimal frequency depends on your goals, resources, and capacity for content capture. We recommend starting with consistency over volume.' },
        { question: 'Do we need to appear on camera?', answer: 'Not necessarily. Many successful TikTok accounts use voiceover, screen recording, product shots, or creative formats. We develop an approach that works for your comfort level.' },
        { question: 'How quickly can we expect growth?', answer: 'TikTok can move fast, but sustainable growth typically builds over 3-6 months. We focus on building a foundation that compounds, not viral spikes that fade.' },
        { question: 'Do you follow every trend?', answer: 'No. We participate in trends strategically—only when they align with your brand and content pillars. Random trend-chasing does not build sustainable audiences.' },
        { question: 'Can you help with monetization?', answer: 'Yes. We advise on Creator Fund, brand partnerships, product promotion, and audience monetization strategies. TikTok Shop management is available as an add-on.' },
        { question: 'What do you need from us?', answer: 'Depends on content type. Minimum: brand assets and approval on content. Some accounts require regular footage capture from you. We define requirements during strategy.' }
      ]
    }
  },
  tr: {
    // Turkish translations would go here - abbreviated for length
    aiSystems: {
      id: 'ai-systems',
      title: 'Yapay Zeka Sistemleri ve Otomasyon',
      shortTitle: 'Yapay Zeka',
      tagline: 'Çalışan Akıllı Altyapı',
      heroDescription: 'Verimsizliği ortadan kaldıran ve operasyonları ölçeklendiren kurumsal düzeyde yapay zeka sistemleri ve iş akışı otomasyonu. İşinize özel pipeline\'lar, entegrasyonlar ve akıllı süreçler inşa ediyoruz.'
    }
    // ... other Turkish translations
  }
};

export const getServiceData = (language, serviceId) => {
  const lang = servicesData[language] || servicesData.en;
  return lang[serviceId] || null;
};

export const getAllServices = (language) => {
  const lang = servicesData[language] || servicesData.en;
  return Object.values(lang);
};
